'use client';

import { motion } from 'framer-motion';
import { useRef, useState } from 'react';
import { Calculator, FileSpreadsheet, TrendingUp, HandCoins, ArrowRight } from 'lucide-react';
import Link from 'next/link';
import styles from './CorporateServices.module.css';

const services = [
    {
        category: "Accounting & Bookkeeping",
        icon: <Calculator size={32} />,
        items: [
            "Cloud Accounting Setup",
            "On-site Bookkeeping",
            "Backlog Cleanup",
            "Financial Reporting"
        ],
        link: "/services/accounting-bookkeeping"
    },
    {
        category: "VAT Services",
        icon: <FileSpreadsheet size={32} />,
        items: [
            "VAT Registration",
            "Return Filing",
            "VAT Refunds",
            "Deregistration"
        ],
        link: "/services/vat-consultancy"
    },
    {
        category: "Corporate Tax",
        icon: <HandCoins size={32} />,
        items: [
            "Tax Registration",
            "CT Assessment",
            "Compliance & Filing",
            "Group Advisory"
        ],
        link: "/services/corporate-tax"
    },
    {
        category: "Financial Advisory",
        icon: <TrendingUp size={32} />,
        items: [
            "CFO Services",
            "Business Restructuring",
            "Financial Modeling",
            "M&A Advisory"
        ],
        link: "/services/corporate-finance-advisory"
    }
];

export default function CorporateServices() {
    return (
        <section className={styles.section}>
            <div className={styles.bgPattern} />

            <div className="container">
                <div className={styles.header}>
                    <motion.span
                        initial={{ opacity: 0, scale: 0.9 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        className={styles.label}
                    >
                        Ongoing Support
                    </motion.span>
                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.1 }}
                        className={styles.title}
                    >
                        We Stay With You <br />
                        <span style={{ color: 'var(--rb-green)' }}>After Setup</span>
                    </motion.h2>
                    <p className={styles.subtitle}>
                        Comprehensive financial, legal, and strategic support to ensure your business grows compliantly in the UAE.
                    </p>
                </div>

                <div className={styles.grid}>
                    {services.map((service, i) => (
                        <ServiceCard key={i} service={service} index={i} />
                    ))}
                </div>
            </div>
        </section>
    );
}

function ServiceCard({ service, index }: any) {
    const cardRef = useRef<HTMLDivElement>(null);
    const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!cardRef.current) return;
        const rect = cardRef.current.getBoundingClientRect();
        setMousePos({
            x: e.clientX - rect.left,
            y: e.clientY - rect.top,
        });
    };

    return (
        <Link href={service.link} style={{ textDecoration: 'none' }}>
            <motion.div
                ref={cardRef}
                onMouseMove={handleMouseMove}
                className={`${styles.card} glass-card-hover`}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
            >
                <div
                    className={styles.cardGlow}
                    style={{ left: mousePos.x, top: mousePos.y }}
                />

                <div className={styles.cardHeader}>
                    <div className={styles.iconBox}>
                        {service.icon}
                    </div>
                    <ArrowRight className={styles.arrowIcon} size={24} />
                </div>

                <h3 className={styles.cardTitle}>{service.category}</h3>

                <div className={styles.serviceList}>
                    {service.items.map((item: any, idx: number) => (
                        <div key={idx} className={styles.serviceItem}>
                            <span className={styles.dot} />
                            {item}
                        </div>
                    ))}
                </div>
            </motion.div>
        </Link>
    );
}
